import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';
import { CardContent, Button, Divider, IconButton, TableContainer, TableHead, Table, TableRow, TableCell, TableBody, TablePagination } from '@material-ui/core';
import styled from 'styled-components';
import { PageTemplateDashboard, PageTemplateCardOver, AppSnackbar } from '@sierralabs/core-ui';
import { Add, Check, Delete, DeleteForever } from '@material-ui/icons';
import CreateEntitySampleDataDialog from '../dialogs/CreateEntitySampleDataDialog';
import DeleteEntityDataDialog from '../dialogs/DeleteEntityDataDialog';
import TruncateEntityDialog from '../dialogs/TruncateEntityDialog';
import { useFindEntityData } from '../hooks/useFindEntityData';
import EditEntityDataDialog from '../dialogs/EditEntityDataDialog';
import { useTruncateEntity } from '../hooks/useTruncateEntity';
const StyledTable = styled(Table) `
  .nowrap {
    white-space: nowrap;
  }
`;
const StyledHeader = styled.div `
  display: flex;
  align-items: center;
  .MuiButtonBase-root:not(:last-child) {
    margin-right: 8px;
  }
`;
const EntityDataList = ({ entity }) => {
    const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
    const [limit, setLimit] = useQueryParam('limit', withDefault(NumberParam, 100));
    const [didDelete, setDidDelete] = useState(false);
    const [didTruncate, setDidTruncate] = useState(false);
    const [editEntityData, setEditEntityData] = useState();
    const [deleteData, setDeleteData] = useState();
    const [isOpenTruncateDialog, setIsOpenTruncateDialog] = useState(false);
    const [isOpenSampleDataDialog, setIsOpenSampleDataDialog] = useState(false);
    const { entityData, getEntityData } = useFindEntityData(entity.tableName);
    const [openEntityDataDialog, setOpenEntityDataDialog] = useState(false);
    const { response, isTruncating, truncateError, truncate } = useTruncateEntity(entity.tableName);
    const getEntityDataWithParams = useCallback(() => {
        getEntityData({ page, limit });
    }, [getEntityData, page, limit]);
    useEffect(() => {
        getEntityDataWithParams();
    }, [getEntityDataWithParams]);
    const getObjectItem = (data) => {
        let value;
        return (data &&
            Object.keys(data)
                .map(itemKey => {
                value = data[itemKey];
                if (value && typeof value === 'object') {
                    if (value.length > 0) {
                        return ` ${itemKey}: {${value.map((item) => {
                            return Object.keys(item).map((subItem) => {
                                return `${subItem}: ${item[subItem] || 'null'}`;
                            });
                        })}}`;
                    }
                    else {
                        let subItem;
                        Object.keys(value).map(item => {
                            subItem = `${item}: ${value[item] || 'null'}`;
                        });
                        return ` ${itemKey}: {${subItem}}`;
                    }
                }
                else {
                    return ` ${itemKey}: ${value || 'null'}`;
                }
            })
                .join(','));
    };
    return (React.createElement(PageTemplateDashboard, null,
        React.createElement(PageTemplateCardOver, null,
            React.createElement(CardContent, null,
                React.createElement(StyledHeader, null,
                    React.createElement(Button, { variant: 'outlined', endIcon: React.createElement(Add, null), onClick: () => {
                            setEditEntityData(undefined);
                            setOpenEntityDataDialog(true);
                        } }, "Add"),
                    React.createElement(Button, { variant: 'outlined', endIcon: React.createElement(Add, null), disabled: !entity.editable, onClick: () => setIsOpenSampleDataDialog(true) }, "Create Sample Data"),
                    React.createElement(Button, { variant: 'outlined', endIcon: React.createElement(DeleteForever, null), disabled: !entity.editable, onClick: () => setIsOpenTruncateDialog(true) }, "Truncate"))),
            React.createElement(Divider, null),
            React.createElement(TableContainer, null,
                React.createElement(StyledTable, { stickyHeader: true },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            entity.columns.map(column => (React.createElement(TableCell, { key: column.name }, column.displayName))),
                            React.createElement(TableCell, null))),
                    React.createElement(TableBody, null, entityData === null || entityData === void 0 ? void 0 : entityData.data.map((item, key) => (React.createElement(TableRow, { hover: true, key: key, onClick: () => {
                            setEditEntityData(item);
                            setOpenEntityDataDialog(true);
                        } },
                        entity.columns.map(column => (React.createElement(TableCell, { key: column.name }, (() => {
                            const data = item[column.name] || '';
                            switch (column.type) {
                                case 'timestamp':
                                    return data
                                        ? format(data, 'MMM d, yyyy h:mm a')
                                        : '';
                                case 'jsonb':
                                    if (Array.isArray(data)) {
                                        return data
                                            .map((items, key) => {
                                            return ` ${key}: {${getObjectItem(items)}}`;
                                        })
                                            .join(',');
                                    }
                                    else {
                                        return getObjectItem(data);
                                    }
                                default:
                                    if (typeof data === 'boolean') {
                                        return data ? React.createElement(Check, null) : '';
                                    }
                                    else {
                                        return data;
                                    }
                            }
                        })()))),
                        React.createElement(TableCell, null,
                            React.createElement(IconButton, { size: 'small', onClick: event => {
                                    event.stopPropagation();
                                    setDeleteData(item);
                                } },
                                React.createElement(Delete, null))))))))),
            React.createElement(TablePagination, { component: 'div', page: page, rowsPerPage: limit, count: (entityData === null || entityData === void 0 ? void 0 : entityData.total) || 0, rowsPerPageOptions: [10, 25, 50, 100], onPageChange: (_, newPage) => setPage(newPage), onRowsPerPageChange: event => {
                    setLimit(+event.target.value);
                    setPage(0);
                } })),
        openEntityDataDialog && (React.createElement(EditEntityDataDialog, { entityData: editEntityData, entity: entity, open: true, onCancel: () => setOpenEntityDataDialog(false), onSave: () => {
                setOpenEntityDataDialog(false);
                getEntityDataWithParams();
            } })),
        !!entity.editable && isOpenSampleDataDialog && (React.createElement(CreateEntitySampleDataDialog, { open: true, entity: entity, onCancel: () => setIsOpenSampleDataDialog(false), onGenerate: () => {
                setIsOpenSampleDataDialog(false);
                getEntityDataWithParams();
            } })),
        React.createElement(TruncateEntityDialog, { entity: entity, open: !!entity.editable && isOpenTruncateDialog, onCancel: () => setIsOpenTruncateDialog(false), onTruncate: () => {
                setDidTruncate(true);
                setIsOpenTruncateDialog(false);
                getEntityDataWithParams();
            } }),
        React.createElement(DeleteEntityDataDialog, { entity: entity, data: deleteData, open: !!deleteData, onCancel: () => setDeleteData(undefined), onDelete: () => {
                setDidDelete(true);
                setDeleteData(undefined);
                getEntityDataWithParams();
            } }),
        React.createElement(AppSnackbar, { severity: 'success', open: didTruncate, onClose: () => setDidTruncate(false) },
            "\"",
            entity.name,
            "\" entity truncated successfully."),
        React.createElement(AppSnackbar, { severity: 'success', open: didDelete, onClose: () => setDidDelete(false) }, "Entity data deleted successfully.")));
};
export default EntityDataList;
//# sourceMappingURL=EntityDataList.js.map